// Module Management Page with Modal for Add/Edit
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Space, Button, Tag, Modal, notification, Card, Spin } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  ThemeButton,
  ThemeCard,
  ThemeFilterBtn,
  ThemeForm,
  ThemeInput,
  ThemeMainWrapper,
  ThemeTable,
  ThemeTableCard,
  ThemeModal,
} from '../Styles/Theme';
import {
	ForbiddenWrapper,
	SubModuleCard,
	SubModuleWrapper,
	DeleteIconWrapper,
	AddSubModuleButton,
} from './ModuleManagementStyle';
import MobHeader from '../../Components/Header/MobHeader';
import { GreetingText } from '../Dashboard/DashboardStyle';
import {
  getModulesApi,
  getModulesReset,
  createModuleApi,
  createModuleReset,
  updateModuleApi,
  updateModuleReset,
  getModuleByIdApi,
  getModuleByIdReset,
  deleteModuleApi,
  deleteModuleReset,
} from '../../store/actions/moduleAction';
import { useModulePermissions } from '../../utils/permissions';
import { DeleteOutlined as DeleteIcon } from '@ant-design/icons';

const ModuleManagement = () => {
  const dispatch = useDispatch();
  const { getModulesState, createModuleState, updateModuleState, getModuleByIdState, deleteModuleState } = useSelector((state) => state.module);

  const [form] = ThemeForm.useForm();
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    search: '',
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [modalOpen, setModalOpen] = useState([false, '', null]); // [isOpen, title, editId]
  const [formData, setFormData] = useState({
    module_name: '',
    module_slug: '',
    sub_modules: [],
  });
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Check permissions using hook
  const { can, permissions } = useModulePermissions();
  const canCreate = can('create');
  const canEdit = can('edit');
  const canDelete = can('delete');

  const isEdit = modalOpen[2] !== null;

  // Fetch modules
  useEffect(() => {
    if (permissions.authorized) {
      dispatch(getModulesApi(filters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, permissions.authorized]);

  // Handle modules response
  useEffect(() => {
    if (getModulesState.apiState === 'success' && getModulesState.data) {
      // Handle both response structures: { modules: [...] } or { data: { modules: [...] } } or { all_modules: [...] }
      const modulesData = getModulesState.data.data || getModulesState.data;
      const modules = modulesData.modules || modulesData.all_modules || [];
      if (modules && modules.length > 0) {
        setTotalRecords(modulesData.pagination?.total || modulesData.total_count || getModulesState.data.pagination?.total || getModulesState.data.total_count || 0);
        const formattedData = modules.map((module) => ({
          key: module._id,
          id: module._id,
          module_name: module.module_name,
          module_slug: module.module_slug,
          type: module.type,
          sub_modules_count: module.sub_modules?.length || 0,
          createdAt: module.createdAt,
        }));
        setTableData(formattedData);
      } else {
        setTableData([]);
        setTotalRecords(0);
      }
      dispatch(getModulesReset());
    } else if (getModulesState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getModulesState.message || 'Failed to fetch modules',
      });
      dispatch(getModulesReset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getModulesState]);

  // Fetch module data when editing
  useEffect(() => {
    if (modalOpen[0] && modalOpen[2]) {
      setIsModalLoading(true);
      dispatch(getModuleByIdApi(modalOpen[2]));
    }
  }, [dispatch, modalOpen]);

  // Handle module data response for edit
  useEffect(() => {
    if (getModuleByIdState.apiState === 'loading') {
      setIsModalLoading(true);
    } else if (getModuleByIdState.apiState === 'success' && getModuleByIdState.data && modalOpen[2]) {
      const module = getModuleByIdState.data;
      const subModules = (module.sub_modules || []).map((subMod) => ({
        _id: subMod._id || subMod.id,
        sub_module_name: subMod.sub_module_name,
        sub_module_slug: subMod.sub_module_slug,
        delete_sub_module: false,
      }));
      const moduleData = {
        module_name: module.module_name || '',
        module_slug: module.module_slug || '',
        sub_modules: subModules,
      };
      setFormData(moduleData);
      form.setFieldsValue(moduleData);
      setIsModalLoading(false);
      dispatch(getModuleByIdReset());
    } else if (getModuleByIdState.apiState === 'error') {
      setIsModalLoading(false);
      notification.error({
        message: 'Error',
        description: getModuleByIdState.message || 'Failed to fetch module',
      });
      dispatch(getModuleByIdReset());
      handleCloseModal();
    }
  }, [getModuleByIdState, dispatch, form, modalOpen]);

  // Handle create response
  useEffect(() => {
    if (createModuleState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: createModuleState.message || 'Module created successfully',
      });
      dispatch(createModuleReset());
      handleCloseModal();
      dispatch(getModulesApi(filters));
    } else if (createModuleState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: createModuleState.message || 'Failed to create module',
      });
      dispatch(createModuleReset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createModuleState]);

  // Handle update response
  useEffect(() => {
    if (updateModuleState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: updateModuleState.message || 'Module updated successfully',
      });
      dispatch(updateModuleReset());
      handleCloseModal();
      dispatch(getModulesApi(filters));
    } else if (updateModuleState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: updateModuleState.message || 'Failed to update module',
      });
      dispatch(updateModuleReset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateModuleState]);

  // Handle delete response
  useEffect(() => {
    if (deleteModuleState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: deleteModuleState.message || 'Module deleted successfully',
      });
      dispatch(deleteModuleReset());
      setDeleteModalVisible(false);
      setSelectedModule(null);
      dispatch(getModulesApi(filters));
    } else if (deleteModuleState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: deleteModuleState.message || 'Failed to delete module',
      });
      dispatch(deleteModuleReset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteModuleState]);

  const handleSearch = (value) => {
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleTableChange = (pagination) => {
    setFilters({
      ...filters,
      page: pagination.current,
      limit: pagination.pageSize,
    });
  };

  // Default empty form data
  const defaultFormData = {
    module_name: '',
    module_slug: '',
    sub_modules: [],
  };

  const handleAdd = () => {
    // Reset form and state before opening modal
    setFormData({ ...defaultFormData });
    form.resetFields();
    form.setFieldsValue({ ...defaultFormData });
    setIsModalLoading(false);
    setModalOpen([true, 'Add Module', null]);
  };

  const handleEdit = (record) => {
    // Reset form before fetching edit data to prevent stale data showing
    setFormData({ ...defaultFormData });
    form.resetFields();
    form.setFieldsValue({ ...defaultFormData });
    setIsModalLoading(true);
    setModalOpen([true, 'Edit Module', record.id]);
  };

  const handleCloseModal = () => {
    // Reset everything on close
    form.resetFields();
    setFormData({ ...defaultFormData });
    setIsModalLoading(false);
    setModalOpen([false, '', null]);
  };

  const handleDelete = (record) => {
    setSelectedModule(record);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    if (selectedModule) {
      dispatch(deleteModuleApi(selectedModule.id));
    }
  };

  // Auto-generate slug from module name
  const handleModuleNameChange = (e) => {
    const value = e.target.value;
    const slug = value.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
    setFormData((prev) => ({
      ...prev,
      module_name: value,
      module_slug: slug,
    }));
    form.setFieldsValue({
      module_name: value,
      module_slug: slug,
    });
  };

  // Auto-generate slug from sub-module name
  const handleSubModuleNameChange = (index, value) => {
    const slug = value.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
    setFormData((prevFormData) => {
      const updatedSubModules = prevFormData.sub_modules ? [...prevFormData.sub_modules] : [];
      
      if (index < updatedSubModules.length) {
        // Update existing sub-module
        updatedSubModules[index] = {
          ...updatedSubModules[index],
          sub_module_name: value,
          sub_module_slug: slug,
        };
      } else {
        // Add new sub-module
        updatedSubModules.push({
          sub_module_name: value,
          sub_module_slug: slug,
        });
      }

      form.setFieldsValue({ sub_modules: updatedSubModules });
      return {
        ...prevFormData,
        sub_modules: updatedSubModules,
      };
    });
  };

  // Mark sub-module for deletion
  const handleDeleteSubModule = (index) => {
    setFormData((prevFormData) => {
      const updatedSubModules = prevFormData.sub_modules ? [...prevFormData.sub_modules] : [];
      
      if (index < updatedSubModules.length) {
        if (updatedSubModules[index]._id) {
          // Existing sub-module - mark for deletion
          updatedSubModules[index].delete_sub_module = true;
        } else {
          // New sub-module - remove from array
          updatedSubModules.splice(index, 1);
        }
      }

      form.setFieldsValue({ sub_modules: updatedSubModules });
      return {
        ...prevFormData,
        sub_modules: updatedSubModules,
      };
    });
  };

  const handleSubmit = (values) => {
    const submitData = {
      ...values,
      ...(isEdit && { id: modalOpen[2] }),
    };

    if (isEdit) {
      dispatch(updateModuleApi(modalOpen[2], submitData));
    } else {
      dispatch(createModuleApi(submitData));
    }
  };

  const columns = [
    {
      title: 'Module Name',
      dataIndex: 'module_name',
      key: 'module_name',
      sorter: true,
    },
    {
      title: 'Module Slug',
      dataIndex: 'module_slug',
      key: 'module_slug',
      render: (slug) => <Tag color="blue">{slug}</Tag>,
    },
    {
      title: 'Sub Modules',
      dataIndex: 'sub_modules_count',
      key: 'sub_modules_count',
      render: (count) => `${count} sub-module(s)`,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          {canEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (!permissions.authorized) {
    return (
      <ThemeMainWrapper>
        <MobHeader />
        <ForbiddenWrapper>
          <h2>403 - Forbidden</h2>
          <p>You do not have permission to access this page.</p>
        </ForbiddenWrapper>
      </ThemeMainWrapper>
    );
  }

  return (
    <>
      <MobHeader />
      <div className="customContainer">
        <Row gutter={[16, 16]} className="mb-3" align="middle">
          <Col xs={20} sm={12} md={12} lg={12}>
            <GreetingText>Module Management</GreetingText>
          </Col>
          <Col xs={4} sm={12} md={12} lg={12} className="text-end">
            {canCreate && (
              <>
                {isMobile ? (
                  <ThemeButton
                    size="large"
                    shape="square"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                  />
                ) : (
                  <ThemeButton
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                  >
                    New Module
                  </ThemeButton>
                )}
              </>
            )}
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-1">
          <Col xs={24} sm={12} md={8} lg={8}>
            <ThemeInput
              placeholder="Search modules..."
              size="large"
              prefix={<SearchOutlined />}
              allowClear
              onPressEnter={(e) => handleSearch(e.target.value)}
              onChange={(e) => {
                if (!e.target.value) {
                  handleSearch('');
                }
              }}
            />
          </Col>
          {/* <Col xs={24} sm={12} md={8} lg={8}>
            <ThemeFilterBtn size="large" shape="square">
              Filter
            </ThemeFilterBtn>
          </Col> */}
        </Row>
      </div>
      <div className="customBorders">
        <ThemeTable
          columns={columns}
          dataSource={tableData}
          loading={getModulesState.apiState === 'loading'}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: totalRecords,
            showSizeChanger: true,
            showTotal: total => `Total ${total} modules`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </div>

      {/* Add/Edit Modal */}
      <ThemeModal
        title={modalOpen[1]}
        centered
        open={modalOpen[0]}
        onCancel={handleCloseModal}
        footer={null}
        width={850}
      >
        {isModalLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading...</div>
          </div>
        ) : (
          <ThemeForm
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={formData}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <ThemeForm.Item
                  label="Module Name"
                  name="module_name"
                  rules={[{ required: true, message: 'Please enter module name' }]}
                >
                  <ThemeInput
                    size="large"
                    placeholder="Enter Module Name"
                    onChange={handleModuleNameChange}
                  />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12}>
                <ThemeForm.Item
                  label="Module Slug"
                  name="module_slug"
                >
                  <ThemeInput
                    size="large"
                    placeholder="Module Slug"
                    disabled
                    value={formData.module_slug}
                  />
                </ThemeForm.Item>
              </Col>
            </Row>

            <ThemeForm.List name="sub_modules">
              {(fields, { add, remove }) => (
                <>
                  <Row gutter={[16, 16]}>
                    {fields.map(({ key, name, ...restField }, index) => {
                      const subModule = formData.sub_modules?.[index];
                      const isMarkedForDelete = subModule?.delete_sub_module;
                      
                      if (isMarkedForDelete) {
                        return null; // Don't render deleted sub-modules
                      }

                      return (
                        <Col xs={24} sm={12} lg={8} key={key} className="mb-4">
                          <SubModuleCard>
                            <SubModuleWrapper>
                              <DeleteIconWrapper
                                onClick={() => {
                                  handleDeleteSubModule(index);
                                  remove(name);
                                }}
                              >
                                <DeleteIcon />
                              </DeleteIconWrapper>
                            </SubModuleWrapper>
                            <ThemeForm.Item
                              {...restField}
                              name={[name, 'sub_module_name']}
                              label="Sub Module Name"
                              rules={[{ required: true, message: 'Please enter sub module name' }]}
                            >
                              <ThemeInput
                                size="large"
                                placeholder="Enter Sub Module Name"
                                onChange={(e) => handleSubModuleNameChange(index, e.target.value)}
                              />
                            </ThemeForm.Item>
                            <ThemeForm.Item
                              {...restField}
                              name={[name, 'sub_module_slug']}
                              label="Sub Module Slug"
                            >
                              <ThemeInput
                                size="large"
                                placeholder="Sub Module Slug"
                                disabled
                                value={formData.sub_modules?.[index]?.sub_module_slug || ''}
                              />
                            </ThemeForm.Item>
                            {subModule?._id && (
                              <ThemeForm.Item
                                {...restField}
                                name={[name, '_id']}
                                hidden
                              >
                                <ThemeInput type="hidden" />
                              </ThemeForm.Item>
                            )}
                          </SubModuleCard>
                        </Col>
                      );
                    })}
                  </Row>
                  <Row>
                    <Col xs={24}>
                      <AddSubModuleButton
                        type="dashed"
                        onClick={() => add()}
                        block
                      >
                        + Add Sub Module
                      </AddSubModuleButton>
                    </Col>
                  </Row>
                </>
              )}
            </ThemeForm.List>

            <Row gutter={[16, 16]} className="mt-4">
              <Col xs={24} className="text-end">
                <Space>
                  <Button onClick={handleCloseModal}>Cancel</Button>
                  <ThemeButton
                    type="primary"
                    htmlType="submit"
                    loading={
                      createModuleState.apiState === 'loading' ||
                      updateModuleState.apiState === 'loading'
                    }
                  >
                    {isEdit ? 'Update Module' : 'Create Module'}
                  </ThemeButton>
                </Space>
              </Col>
            </Row>
          </ThemeForm>
          )}
        </ThemeModal>

      {/* Delete Modal */}
      <Modal
        title="Delete Module"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedModule(null);
        }}
      >
        <p>
          Are you sure you want to delete module "{selectedModule?.module_name}
          "?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
    </>
  );
};

export default ModuleManagement;
